import { useEffect, useState } from 'react';
import { Box, Typography, Grid, Card, CardContent, CardActions, Button, Container, Chip, Tabs, Tab, TextField, Paper } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import SearchIcon from '@mui/icons-material/Search';
import { HelpItem } from '../../../../Models/HelpAndResources/HelpItem.model';
import { HelpItemTypeEnum } from '../../../../Enum/HelpItemTypeEnum';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import { getAllHelpItems } from '../../../../store/helpAndResources.slice';

const Help = ({ title }: { title: string }) => {
    const dispatch = useAppDispatch();
    const params = window.location;

    const { allHelpItems } = useSelector((state: any) => state.helpAndResources);
    const [tabValue, setTabValue] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        dispatch(getAllHelpItems());
        document.title = title;
    }, [dispatch, title]);

    const handleOpenLink = (item: HelpItem) => {
        const url = item.Url;
        window.open(url, '_blank');
    };

    const helpVideos = allHelpItems.filter((item) => item.Type === HelpItemTypeEnum.VIDEO);
    const helpDocuments = allHelpItems.filter((item) => item.Type === HelpItemTypeEnum.DOCUMENT);
    const allItems = tabValue === 0 ? helpDocuments : helpVideos;

    const filteredItems = allItems.filter(
        (item) => item.Title.toLowerCase().includes(searchQuery.toLowerCase()) || item.Description.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <Container maxWidth="lg" sx={{ py: 8 }}>
            <Box sx={{ mb: 6, textAlign: 'center' }}>
                <Typography variant="h4" gutterBottom fontWeight="800" color="primary" sx={{ mb: 2 }}>
                    Help & Resources
                </Typography>

                <Paper
                    elevation={3}
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        maxWidth: 600,
                        mx: 'auto',
                        backgroundColor: 'background.paper',
                    }}
                >
                    <TextField
                        fullWidth
                        placeholder="Search for documents or videos..."
                        variant="outlined"
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </Paper>
            </Box>

            <Box sx={{ mb: 6, display: 'flex', justifyContent: 'center' }}>
                <Tabs
                    value={tabValue}
                    onChange={(e, newValue) => setTabValue(newValue)}
                    centered
                    indicatorColor="primary"
                    textColor="primary"
                    sx={{
                        '& .MuiTab-root': { fontWeight: 'bold', fontSize: '1.1rem' },
                        '& .MuiTabs-indicator': { bottom: 2 },
                    }}
                >
                    <Tab label="Documents" />
                    <Tab label="Videos" />
                </Tabs>
            </Box>

            <Grid container spacing={4}>
                {filteredItems.length > 0 ? (
                    filteredItems.map((item, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 3,
                                    transition: 'all 0.3s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: 10,
                                        borderColor: 'primary.main',
                                    },
                                    border: '1px solid',
                                    borderColor: 'transparent',
                                }}
                            >
                                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        {item?.Type === HelpItemTypeEnum.DOCUMENT ? (
                                            <DescriptionIcon color="primary" fontSize="large" />
                                        ) : (
                                            <PlayCircleOutlineIcon color="secondary" fontSize="large" />
                                        )}
                                        <Chip
                                            label={HelpItemTypeEnum[item?.Type]}
                                            size="small"
                                            variant="outlined"
                                            color={item?.Type === HelpItemTypeEnum.DOCUMENT ? 'primary' : 'secondary'}
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    </Box>
                                    <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                        {item?.Title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                        {item?.Description}
                                    </Typography>
                                </CardContent>
                                <CardActions sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="small"
                                        endIcon={item?.Type === HelpItemTypeEnum.DOCUMENT ? <DescriptionIcon /> : <VideoLibraryIcon />}
                                        onClick={() => handleOpenLink(item)}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        {item?.Type === HelpItemTypeEnum.DOCUMENT ? 'View PDF' : 'Watch Video'}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))
                ) : (
                    <Grid size={{ xs: 12 }}>
                        <Box sx={{ py: 10, textAlign: 'center' }}>
                            <Typography variant="h6" color="text.secondary">
                                No resources found matching "{searchQuery}"
                            </Typography>
                        </Box>
                    </Grid>
                )}
            </Grid>
        </Container>
    );
};

export default Help;
